import React, { useState, Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, Globe, Medal, Play } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function TeamOverview() {
  const {
    t
  } = useLanguage();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const achievements = [{
    icon: <Trophy size={32} className="text-brazil-yellow mb-4" data-id="element-316" />,
    title: t('team.stat1'),
    desc: t('team.stat1Desc')
  }, {
    icon: <Medal size={32} className="text-brazil-yellow mb-4" data-id="element-317" />,
    title: t('team.stat2'),
    desc: t('team.stat2Desc')
  }, {
    icon: <Globe size={32} className="text-brazil-yellow mb-4" data-id="element-318" />,
    title: t('team.stat3'),
    desc: t('team.stat3Desc')
  }, {
    icon: <Star size={32} className="text-brazil-yellow mb-4" data-id="element-319" />,
    title: t('team.stat4'),
    desc: t('team.stat4Desc')
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <>
      <section id="team" className="py-12 md:py-24 bg-brazil-black relative" data-id="element-320">
        {/* Decorative background element */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-brazil-dark-green to-transparent opacity-50" data-id="element-321"></div>

        <div className="container mx-auto px-6 md:px-12" data-id="element-322">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-323">
            {/* Text Content */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} data-id="element-324">
              <h2 className="text-brazil-green font-oswald uppercase tracking-widest text-sm font-bold mb-3" data-id="element-325">
                {t('team.label')}
              </h2>
              <h3 className="text-4xl md:text-5xl font-oswald font-bold uppercase leading-tight mb-6" data-id="element-326">
                {t('team.title1')} <br data-id="element-327" />
                <span className="text-brazil-yellow" data-id="element-328">{t('team.title2')}</span>
              </h3>
              <p className="text-gray-400 text-lg leading-relaxed mb-8" data-id="element-329">
                {t('team.desc')}
              </p>

              <div className="flex items-center gap-4 mb-8" data-id="element-330">
                <div className="w-12 h-1 bg-brazil-green" data-id="element-331"></div>
                <span className="font-oswald uppercase tracking-widest text-sm text-gray-300" data-id="element-332">
                  {t('team.since')}
                </span>
              </div>

              <a href="https://en.wikipedia.org/wiki/Brazil_national_football_team" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-brazil-green/10 hover:bg-brazil-green/20 border border-brazil-green/30 text-white px-6 py-3 rounded-full font-oswald uppercase tracking-wider text-sm font-bold transition-all duration-300 min-h-[44px]" data-id="element-333">
                {t('team.history')}
              </a>
            </motion.div>

            {/* Achievement Grid */}
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} className="grid grid-cols-2 gap-4 md:gap-6" data-id="element-334">
              {achievements.map((item, index) => <motion.div key={index} variants={itemVariants} className="bg-[#111] border border-white/5 p-4 md:p-6 rounded-xl md:rounded-2xl hover:border-brazil-dark-green transition-colors duration-300 group" data-id="element-335">
                  <div className="transform group-hover:scale-110 transition-transform duration-300 origin-left [&>svg]:w-6 [&>svg]:h-6 md:[&>svg]:w-8 md:[&>svg]:h-8" data-id="element-336">
                    {item.icon}
                  </div>
                  <h4 className="font-oswald text-sm md:text-xl font-bold uppercase mb-0.5 md:mb-1 leading-tight" data-id="element-337">
                    {item.title}
                  </h4>
                  <p className="text-[11px] md:text-sm text-gray-400" data-id="element-338">
                    {item.desc}
                  </p>
                </motion.div>)}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Video Embed */}
      <section className="py-10 md:py-16 bg-[#111] relative" data-id="element-339">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-brazil-yellow/30 to-transparent" data-id="element-340"></div>
        <div className="container mx-auto px-4 md:px-12" data-id="element-341">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-6 md:mb-8" data-id="element-342">
            <h2 className="text-brazil-green font-oswald uppercase tracking-widest text-xs md:text-sm font-bold mb-2" data-id="element-343">
              {t('team.videoLabel')}
            </h2>
            <h3 className="text-2xl md:text-4xl font-oswald font-bold uppercase" data-id="element-344">
              <span className="text-brazil-green" data-id="element-345">Green</span> &{' '}
              <span className="text-brazil-yellow" data-id="element-346">Yellow</span>, Strong & Proud
            </h3>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-brazil-dark-green/20" data-id="element-347">
            <div className="relative w-full" style={{
            paddingBottom: '56.25%'
          }} data-id="element-348">
              <iframe src="https://www.youtube.com/embed/EfLkWNqej_8?rel=0&modestbranding=1" title="Brazil Highlights" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-349" />
            </div>
          </motion.div>
        </div>
      </section>
    </>;
}